import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getAllListings } from '../../../services/listings';
import type { Listing } from '../../../services/listings';

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  match: (cat: string, l: Listing) => boolean;
}

const categories: Category[] = [
  { id: 'textbooks', name: 'Textbooks', icon: 'ri-book-open-line', color: 'from-blue-500 to-blue-600', description: 'Course materials & study guides', match: (c) => c.includes('textbook') || c.includes('book') },
  { id: 'electronics', name: 'Electronics', icon: 'ri-smartphone-line', color: 'from-purple-500 to-purple-600', description: 'Phones, laptops & gadgets', match: (c) => c.includes('electronic') || c.includes('phone') || c.includes('laptop') || c.includes('gadget') },
  { id: 'furniture', name: 'Furniture', icon: 'ri-sofa-line', color: 'from-orange-500 to-orange-600', description: 'Dorm & apartment essentials', match: (c) => c.includes('furniture') || c.includes('home') },
  { id: 'clothing', name: 'Clothing', icon: 'ri-shirt-line', color: 'from-pink-500 to-pink-600', description: 'Fashion & accessories', match: (c) => c.includes('cloth') || c.includes('fashion') || c.includes('wear') },
  { id: 'sports', name: 'Sports & Fitness', icon: 'ri-basketball-line', color: 'from-green-500 to-green-600', description: 'Equipment & gear', match: (c) => c.includes('sport') || c.includes('fitness') || c.includes('gym') },
  { id: 'tickets', name: 'Event Tickets', icon: 'ri-ticket-line', color: 'from-red-500 to-red-600', description: 'Concerts, games & events', match: (c, l) => l.type === 'ticket' || c.includes('ticket') || c.includes('event') },
];

const CategorySection = () => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [images, setImages] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      try {
        const listings = await getAllListings();
        const nextCounts: Record<string, number> = {};
        const nextImages: Record<string, string> = {};
        for (const category of categories) {
          for (const l of listings) {
            const cat = (l.category || '').toLowerCase();
            if (!category.match(cat, l)) continue;
            nextCounts[category.id] = (nextCounts[category.id] || 0) + 1;
            const img = l.images?.[0] || (l as any).image;
            if (img && !nextImages[category.id]) nextImages[category.id] = img;
          }
        }
        setCounts(nextCounts);
        setImages(nextImages);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <section className="py-14 md:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Shop by Category
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Find exactly what you need from fellow students across campus
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {categories.map((category) => {
            const img = images[category.id];
            const count = counts[category.id] || 0;
            return (
              <Link
                key={category.id}
                to={`/marketplace?category=${category.id}`}
                className="group relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative aspect-square">
                  {/* Gradient tile is always the base so a missing/broken image still looks intentional */}
                  <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${category.color}`}>
                    <i className={`${category.icon} text-white text-4xl opacity-90`}></i>
                  </div>
                  {img && (
                    <img
                      src={img}
                      alt={category.name}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  )}
                  {/* Legibility gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent"></div>

                  {/* Icon chip */}
                  <div className="absolute left-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm">
                    <i className={`${category.icon} text-teal-600`}></i>
                  </div>

                  {/* Label */}
                  <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                    <h3 className="font-semibold leading-tight">{category.name}</h3>
                    <p className="text-xs text-white/85">{count} {count === 1 ? 'item' : 'items'}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10 md:mt-12">
          <Link
            to="/marketplace"
            className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            View All Categories
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategorySection;
